import { Graphics, Container, ContainerChild, Text } from "pixi.js";

import type {
  IStudioWidget,
  IStudioWidgetCountdown,
} from "../../../../../../screens/Dashboard/SubComponents/Studio/interfaces/sub-interfaces/Widgets.interface";

import { TimerService } from "../../../Services/TimerService";

import { getTimeParts } from "../Countdown.utils";

import type { ICountdownTheme } from "./ICountdownTheme";

export class Theme1Renderer implements ICountdownTheme {
  private timerCallback: ((time: number) => void) | null = null;

  private container: Container | null = null;

  private cardContainers: {
    container: Container;

    valueText: Text;

    labelText: Text;
  }[] = [];

  render(
    widget: IStudioWidget,

    parentContainer: Container,

    destination: { x: number; y: number; width: number; height: number },
  ): void {
    const options = (widget as IStudioWidgetCountdown).options;

    this.container = new Container();

    parentContainer.addChild(this.container);

    const targetDate = options.targetDate
      ? new Date(options.targetDate)
      : new Date();

    const fontFamily = options.fontFamily || "Arial";

    const color = options.color || "#FFFFFF";

    const backgroundColor = options.backgroundColor || "#333333";

    const fontSize = parseInt(options.fontSize ?? "") || 42;

    const bold = options.bold || false;

    const italic = options.italic || false;

    const underline = options.underline || false;

    const strike = options.strike || false;

    const lineHeight = options.lineHeight || 1;

    const wordSpacing = options.wordSpacing || 0;

    const showLabels = options.showLabels !== false;

    const centerX = destination.width / 2;

    const centerY = destination.height / 2;

    // Define all possible time parts

    const allTimeParts = [
      { label: "DAYS", show: options.showDays !== false },

      { label: "HOURS", show: options.showHours !== false },

      { label: "MINUTES", show: options.showMinutes !== false },

      { label: "SECONDS", show: options.showSeconds !== false },
    ];

    // Filter enabled parts

    const enabledParts = allTimeParts.filter((part) => part.show);

    const cardCount = enabledParts.length;

    const cardSize = 80;

    const cardGap = 16;

    const totalWidth = cardCount * cardSize + (cardCount - 1) * cardGap;

    const startX = centerX - totalWidth / 2 + cardSize / 2;

    // Create card containers for each enabled time part

    this.cardContainers = [];

    enabledParts.forEach((part, index) => {
      const cardX = startX + index * (cardSize + cardGap);

      const cardY = centerY;

      // Card background

      const cardBg = new Graphics();

      cardBg.roundRect(
        -cardSize / 2,

        -cardSize / 2,

        cardSize,

        cardSize,

        8,
      );

      cardBg.fill(backgroundColor);

      // Horizontal divider line in the middle

      const dividerLine = new Graphics();

      dividerLine.rect(-cardSize / 2, -0.5, cardSize, 1);

      dividerLine.fill({ color: 0x000000, alpha: 0.2 });

      // Value text (the number)

      const valueText = new Text({
        text: "00",

        style: {
          fontFamily,

          fontSize,

          fill: color,

          align: "center",

          fontWeight: bold ? "bold" : "normal",

          fontStyle: italic ? "italic" : "normal",

          lineHeight,

          letterSpacing: wordSpacing,
        },
      });

      valueText.anchor.set(0.5);

      // Apply text decorations

      if (underline || strike) {
        let decoration = "";

        if (underline) decoration += "underline ";

        if (strike) decoration += "line-through ";

        (valueText.style as unknown as Record<string, string>).textDecoration =
          decoration.trim();
      }

      // Label text (DAYS, HOURS, etc.)

      const labelText = new Text({
        text: part.label,

        style: {
          fontFamily,

          fontSize: 10,

          fill: color,

          align: "center",
        },
      });

      labelText.anchor.set(0.5);

      labelText.position.set(0, cardSize / 2 + 12);

      // Create container for this card

      const cardContainer = new Container();

      cardContainer.position.set(cardX, cardY);

      cardContainer.addChild(cardBg);

      cardContainer.addChild(dividerLine);

      cardContainer.addChild(valueText as unknown as ContainerChild);

      if (showLabels) {
        cardContainer.addChild(labelText as unknown as ContainerChild);
      }

      this.container!.addChild(cardContainer);

      this.cardContainers.push({
        container: cardContainer,

        valueText,

        labelText,
      });
    });

    const timerService = TimerService.getInstance();

    const update = (_now: number) => {
      const { days, hours, minutes, seconds } = getTimeParts(
        targetDate,

        options,
      );

      const timeValues = {
        DAYS: days,

        HOURS: hours,

        MINUTES: minutes,

        SECONDS: seconds,
      };

      // Update each card with its corresponding value

      enabledParts.forEach((part, index) => {
        const value = timeValues[part.label as keyof typeof timeValues];

        const paddedValue = String(value).padStart(2, "0");

        const cardData = this.cardContainers[index];

        if (cardData && cardData.valueText.text !== paddedValue) {
          cardData.valueText.text = paddedValue;
        }
      });
    };

    update(Date.now());

    this.timerCallback = update;

    timerService.subscribe(update);
  }

  destroy(): void {
    if (this.timerCallback) {
      TimerService.getInstance().unsubscribe(this.timerCallback);

      this.timerCallback = null;
    }

    if (this.container) {
      this.container.destroy({ children: true });

      this.container = null;
    }

    this.cardContainers = [];
  }
}
